import React, { useState, useRef, useEffect } from 'react';
import { MapPin, Plus, MessageSquare, LogIn, LogOut, Slack, MoreVertical, Share2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from './ui/sonner';
import { isInAppBrowser } from '../lib/browser';
import OpenInBrowserModal from './OpenInBrowserModal';

const SLACK_URL = 'https://join.slack.com/t/pdxstartups/shared_invite/zt-445l9s8f7-Fu_z2SvH7XmdBN_sWE1HqA';

const UserMenu = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  if (!user) return null;
  const initial = (user.name || user.email || '?').charAt(0).toUpperCase();

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        title={user.email}
        className="inline-flex items-center gap-2 bg-white/95 backdrop-blur-md hover:bg-white text-slate-700 border border-slate-200/60 text-[13px] font-medium pl-1.5 pr-3 py-1.5 rounded-full shadow-md transition-colors"
      >
        {user.picture ? (
          <img src={user.picture} alt="" className="w-6 h-6 rounded-full" referrerPolicy="no-referrer" />
        ) : (
          <span className="w-6 h-6 rounded-full bg-emerald-500 text-white text-[11px] font-semibold inline-flex items-center justify-center">
            {initial}
          </span>
        )}
        <span className="hidden sm:inline max-w-[120px] truncate">{user.name || user.email}</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-56 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-50">
          <div className="px-3 py-2 border-b border-slate-100">
            <div className="text-[13px] font-medium text-slate-900 truncate">{user.name || user.email}</div>
            <div className="text-[11.5px] text-slate-500 truncate">{user.email}</div>
          </div>
          <button
            onClick={() => { logout(); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-[13px] hover:bg-slate-50 text-slate-700 inline-flex items-center gap-2"
          >
            <LogOut size={13} className="text-slate-400" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
};

const MobileActions = ({ onFeedback, onSignIn, onShare, user }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div ref={ref} className="relative sm:hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="mobile-menu-button"
        aria-label="More options"
        className="inline-flex items-center justify-center bg-white/95 backdrop-blur-md hover:bg-white text-slate-700 border border-slate-200/60 p-2 rounded-full shadow-md transition-colors"
      >
        <MoreVertical size={18} strokeWidth={2.25} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-52 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-50">
          <a
            href={SLACK_URL}
            target="_blank"
            rel="noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2.5 text-[13.5px] hover:bg-slate-50 text-slate-700"
          >
            <Slack size={15} className="text-[#611f69]" strokeWidth={2.25} /> Join our Slack
          </a>
          <button
            onClick={() => { onShare(); setOpen(false); }}
            data-testid="mobile-share-button"
            className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-[13.5px] hover:bg-slate-50 text-slate-700"
          >
            <Share2 size={15} className="text-slate-500" strokeWidth={2.25} /> Share this map
          </button>
          {onFeedback && (
            <button
              onClick={() => { onFeedback(); setOpen(false); }}
              className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-[13.5px] hover:bg-slate-50 text-slate-700"
            >
              <MessageSquare size={15} className="text-emerald-600" strokeWidth={2.25} /> Feedback
            </button>
          )}
          {!user && (
            <button
              onClick={() => { onSignIn(); setOpen(false); }}
              data-testid="mobile-sign-in-button"
              className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-[13.5px] hover:bg-slate-50 text-slate-700"
            >
              <LogIn size={15} className="text-slate-500" strokeWidth={2.25} /> Sign in
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const Header = ({ onSubmit, onFeedback }) => {
  const { user, login } = useAuth();
  const [browserHint, setBrowserHint] = useState(false);

  const handleSignIn = () => {
    // Google OAuth is blocked inside in-app browsers — guide the user instead
    // of dead-ending on Google's "disallowed_useragent" screen.
    if (isInAppBrowser()) {
      setBrowserHint(true);
      return;
    }
    login();
  };

  const handleShare = async () => {
    const url = window.location.origin + '/';
    // Native share sheet only on touch devices; desktop always copies + toasts.
    const canNativeShare =
      typeof navigator.share === 'function' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(pointer: coarse)').matches;
    if (canNativeShare) {
      try {
        await navigator.share({
          title: 'Portland Startup Map',
          text: 'Discover startups across Oregon & SW Washington',
          url,
        });
        return;
      } catch (e) {
        if (e && e.name === 'AbortError') return; // user dismissed the sheet
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied — share it anywhere!');
    } catch {
      toast.error('Could not copy the link. Please copy it from the address bar.');
    }
  };

  return (
    <div className="absolute top-0 left-0 right-0 z-[1000] flex items-center justify-between gap-2 px-3 sm:px-4 py-3 pointer-events-none">
      <div className="flex items-center gap-2 bg-white/95 backdrop-blur-md rounded-full pl-3 pr-3 sm:pr-4 py-2 shadow-md pointer-events-auto border border-slate-200/60 min-w-0">
        <MapPin size={18} className="text-slate-900 flex-shrink-0" strokeWidth={2.25} />
        <span className="font-semibold text-slate-900 tracking-tight text-[13px] sm:text-[15px] truncate">
          Portland Startup Map<span className="text-emerald-600">.</span>
        </span>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2 pointer-events-auto flex-shrink-0">
        <a
          href={SLACK_URL}
          target="_blank"
          rel="noreferrer"
          title="Join the PDX Startups Slack"
          className="hidden sm:inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-md hover:bg-white text-slate-700 border border-slate-200/60 text-[13px] font-medium px-3 py-2 rounded-full shadow-md transition-colors"
        >
          <Slack size={14} className="text-[#611f69]" strokeWidth={2.25} />
          <span className="hidden md:inline">Join our Slack</span>
          <span className="md:hidden">Slack</span>
        </a>

        <button
          onClick={handleShare}
          data-testid="share-button"
          title="Copy a shareable link to this map"
          className="hidden sm:inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-md hover:bg-white text-slate-700 border border-slate-200/60 text-[13px] font-medium px-3 py-2 rounded-full shadow-md transition-colors"
        >
          <Share2 size={14} className="text-slate-500" strokeWidth={2.25} />
          Share
        </button>

        {onFeedback && (
          <button
            onClick={onFeedback}
            title="Share feedback"
            className="hidden sm:inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-md hover:bg-white text-slate-700 border border-slate-200/60 text-[13px] font-medium px-3 py-2 rounded-full shadow-md transition-colors"
          >
            <MessageSquare size={14} className="text-emerald-600" strokeWidth={2.25} />
            Feedback
          </button>
        )}

        {user ? (
          <UserMenu />
        ) : (
          <button
            onClick={handleSignIn}
            data-testid="sign-in-button"
            title="Sign in with Google"
            className="hidden sm:inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-md hover:bg-white text-slate-700 border border-slate-200/60 text-[13px] font-medium px-3 py-2 rounded-full shadow-md transition-colors"
          >
            <LogIn size={14} className="text-slate-500" strokeWidth={2.25} />
            Sign in
          </button>
        )}

        <button
          onClick={onSubmit}
          data-testid="submit-startup-button"
          className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-[13px] font-medium px-3.5 sm:px-4 py-2.5 rounded-full shadow-md transition-colors"
        >
          <Plus size={15} strokeWidth={2.5} />
          <span className="hidden sm:inline">Submit your startup</span>
          <span className="sm:hidden">Submit</span>
        </button>

        <MobileActions onFeedback={onFeedback} onSignIn={handleSignIn} onShare={handleShare} user={user} />
      </div>

      <OpenInBrowserModal open={browserHint} onClose={() => setBrowserHint(false)} />
    </div>
  );
};

export default Header;
